import { createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import { pathOr } from 'ramda';
import { validateModel, validateFormField } from '../validation/addressDetails';
import { canProceed } from '../utils/validation/canProceed';
import { personalDetails } from './personalDetails';
import { setDuplicateUserStatus } from './api';

const initialState = {
  validation: {
    ADDRESS: {
      effective_period: {}
    }
  },
  hasTriedToSave: false,
  hasValidatedModel: false,
  isDirty: false,
  isSaving: false,
  isLoading: false,
  canContinue: false,
  model: {
    ADDRESS: {
      effective_period: {
        from: '',
        to: ''
      },
      international_address: {},
      uk_address: {}
    },
    PREVIOUS_ADDRESSES: [],
    residential_status: ''
  },
  personalDetails: {
    birthDate: ''
  }
};

function getEffectivePeriodFromValue(address) {
  if (address && address.effective_period && address.effective_period.from) {
    return address.effective_period.from;
  }

  return null;
}

const addressDetails = createSlice({
  name: 'addressDetails',
  initialState,

  reducers: {
    loadAddressDetails: (state) => {
      state.isLoading = true;
    },
    loadAddressDetailsCompleted: (state, action) => {
      state.model = action.payload;
      state.isLoading = false;
    },
    loadAddressDetailsRejected: (state) => {
      state.isLoading = false;
    },
    modelChange: (state, action) => {
      state.model = action.payload;
      state.isDirty = true;
    },
    fieldChange: (state, { payload: { field, value, section } }) => {
      if (section) {
        state.model[section][field] = value;
      } else {
        switch (field) {
          case 'ADDRESS':
            // Update current address
            state.model.ADDRESS = value;

            // If the move-in-date for current address has changed, need to update
            // previous addresses.
            if (state.model.PREVIOUS_ADDRESSES && state.model.PREVIOUS_ADDRESSES.length > 0) {
              // Ignore if there's no effective_period yet set
              if (state.model.PREVIOUS_ADDRESSES[0].effective_period) {
                state.model.PREVIOUS_ADDRESSES[0].effective_period = {
                  ...state.model.PREVIOUS_ADDRESSES[0].effective_period,
                  to: getEffectivePeriodFromValue(value)
                };
              }
            }
            break;

          case 'PREVIOUS_ADDRESSES': {
            const newValue = value || [];
            const previousLength = state.model.PREVIOUS_ADDRESSES.length;

            state.model.PREVIOUS_ADDRESSES = newValue.map((item, index, newValues) => {
              // If there's no effective period, just use as-is.
              if (!item.effective_period) {
                return item;
              }

              // If this is the first previous address, use current address to work
              // out the effective TO date. Otherwise, use the previous _previous_ address.
              const sourceAddress = index === 0 ? state.model.ADDRESS : newValues[index - 1];

              return {
                ...item,
                effective_period: {
                  ...item.effective_period,
                  to: getEffectivePeriodFromValue(sourceAddress)
                }
              };
            });

            // If something new has been added, reset validation flag
            if (state.model.PREVIOUS_ADDRESSES.length !== previousLength) {
              state.hasValidatedModel = false;
            }
            break;
          }

          default:
            state.model[field] = value;
            break;
        }
      }
      state.isDirty = true;
    },
    tryGoNext: (state, action) => {
      state.validation = validateModel(action.payload, state.personalDetails);
      state.hasValidatedModel = true;
      state.canContinue = canProceed(state.validation);
    },
    validateForReview: (state, action) => {
      state.validation = validateModel(action.payload, state.personalDetails);
    },
    validateField: (state, action) => {
      const { field } = action.payload;

      state.validation = validateFormField(
        field,
        state.model,
        state.personalDetails,
        state.hasValidatedModel,
        state.validation
      );
    },
    resetValidation: (state) => {
      state.validation = initialState.validation;
      state.canContinue = false;
      state.hasValidatedModel = false;
    },
    save: (state) => {
      state.isSaving = true;
      state.hasTriedToSave = true;
    },
    saveCompleted: (state) => {
      state.isSaving = false;
    },
    saveCancelled: (state) => {
      state.isSaving = false;
    },
    saveRejected: (state) => {
      state.isSaving = false;
    },
    resetState: () => initialState,
    overrideResidentialStatus: (state, action) => {
      state.model.residential_status = action.payload;
    },
    updateAddressDetails: (state, action) => {
      const {
        personal_details,
        personal_details: { addresses }
      } = action.payload;
      const [currentAddress, ...previousAddresses] = addresses;
      const previousAddressList = [];
      if (previousAddresses.length > 0) {
        previousAddresses.forEach((previousAddress) => {
          const { effective_period, international_address, uk_address } = previousAddress;
          if (effective_period && effective_period.to) {
            const addresss = {
              effective_period: {
                from: pathOr('', ['from'], effective_period),
                to: pathOr('', ['to'], effective_period)
              },
              international_address: international_address
                ? {
                    address_line_1: international_address.address_line1,
                    address_line_2: international_address.address_line2,
                    address_line_3: international_address.address_line3,
                    address_line_4: international_address.address_line4,
                    country: international_address.country
                  }
                : {},
              uk_address: uk_address || {}
            };
            previousAddressList.push(addresss);
          }
        });
      }

      state.personalDetails.birthDate =
        personal_details.dob && moment(personal_details.dob, 'YYYYMMDD', true).format('YYYY-MM-DD');

      state.model = {
        ...state.model,
        ADDRESS: {
          effective_period: {
            from: currentAddress.effective_period.from || state.model.ADDRESS.effective_period.from,
            to: ''
          },
          international_address:
            currentAddress.international_address || state.model.ADDRESS.international_address,
          uk_address: currentAddress.uk_address || state.model.ADDRESS.uk_address
        },
        PREVIOUS_ADDRESSES: previousAddressList || state.model.PREVIOUS_ADDRESSES,
        residential_status: personal_details.residential_status || state.model.residential_status
      };
    }
  },

  extraReducers: (builder) => {
    builder.addCase(personalDetails.actions.fieldChange, (state, action) => {
      if (action.payload.field === 'birthDate') {
        state.personalDetails.birthDate = action.payload.value;
      }
    });

    builder.addCase(personalDetails.actions.modelChange, (state, action) => {
      if (action.payload.birthDate) {
        state.personalDetails.birthDate = action.payload.birthDate;
      }
    });
  }
});

export function tryToContinue(model, callback) {
  return (dispatch) => {
    dispatch(tryGoNext(model));
    dispatch(checkCanContinue(callback));
    dispatch(setDuplicateUserStatus(false));
  };
}

function checkCanContinue(callback) {
  return (dispatch, getState) => {
    const { canContinue } = getState().addressDetails;
    if (canContinue) {
      if (typeof callback === 'function') {
        callback();
      }
      dispatch(resetValidation());
    }
  };
}
export function confirmChanges(editState, callback) {
  return (dispatch) => {
    dispatch(tryGoNext(editState));
    dispatch(confirmModelChanges(editState, callback));
    dispatch(setDuplicateUserStatus(false));
  };
}

function confirmModelChanges(editState, callback) {
  return (dispatch, getState) => {
    const { canContinue } = getState().addressDetails;
    if (canContinue) {
      dispatch(modelChange(editState));
      if (typeof callback === 'function') {
        callback();
      }
    }
  };
}

export { addressDetails };
export const {
  fieldChange,
  loadAddressDetails,
  updateAddressDetails,
  loadAddressDetailsCompleted,
  loadAddressDetailsRejected,
  modelChange,
  resetValidation,
  save,
  saveCancelled,
  saveCompleted,
  saveRejected,
  tryGoNext,
  validateForReview,
  validateField,
  resetState
} = addressDetails.actions;
